import {
  computeAggregateSubmitStats,
  computeMarketSubmitStats,
  type OfferSubmitStats,
} from '@/enterprise/components/OfferBuilder/utils';
import { GlobalOfferMarketPricingType } from '@/enterprise/data/useGlobalOffer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { ExchangeRates } from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import type { useReporting } from '@/reporting';

export interface SetOfferFormInputs {
  rate: string | null;
  rateType: 'apr' | 'disc' | undefined;
  expireOn: string | null;
}

export interface OfferLimits {
  disc: { minThreshold: number; maxRate: number };
  apr: { minThreshold: number; maxRate: number };
}

interface ValidateRateInput {
  rate: SetOfferFormInputs['rate'];
  rateType: SetOfferFormInputs['rateType'];
  offerLimits: OfferLimits;
}

/**
 * Checks if a offer is valid for global offer update.
 * Global offer updates can include:
 * 1. Updating participation On or Off
 * 2. Updating rate
 *
 * @param tm - The taker market to check.
 * @param isEnabled - Whether participation is being updated to on or off. If undefined, the participation is not being updated.
 * @returns True if the offer is valid for update, false otherwise.
 */
export const isOfferValidForUpdate = (tm: TakerMarket, isEnabled?: boolean) => {
  const { offerConfig, isTpf, marketIsEnabled, offerSchedule } = tm;

  // Check if the offer has a valid UUID
  if (offerConfig.uuid === undefined) {
    return false;
  }

  // Check if the taker market is a TPF market
  if (isTpf === true) {
    return false;
  }

  // Check if the offer is a preferred term or express accept offer
  if (offerConfig.offerType === 'PREFERRED_TERM' || offerConfig.offerType === 'EXPRESS_ACCEPT') {
    return false;
  }

  // Check if market is enabled
  if (marketIsEnabled !== true) {
    return false;
  }

  // Handle scheduled offer logic
  if (isEnabled !== undefined && offerSchedule !== undefined) {
    // If isEnabled is defined, participation is being updated
    // If updating participation, only non-scheduled offers are valid for updates
    return false;
  }

  // If all above checks pass, the offer is valid for update
  return true;
};

/**
 * Aggregates invoice stats for the markets that will actually change state when participation is toggled.
 * Only includes markets that pass `isOfferValidForUpdate` and whose current enabled state differs from `isEnabled`.
 * Amounts are converted to USD for cross-currency consistency.
 */
export const computeParticipationSubmitStats = ({
  takerMarkets,
  isEnabled,
  exchangeRates,
}: {
  takerMarkets: TakerMarket[];
  isEnabled: boolean;
  exchangeRates: ExchangeRates;
}): OfferSubmitStats => {
  const affectedMarkets = takerMarkets.filter(
    (tm) => isOfferValidForUpdate(tm, isEnabled) && tm.offerConfig.isEnabled !== isEnabled
  );

  const totals = affectedMarkets.reduce(
    (acc, tm) => {
      acc.eligibleInvoiceAmount += convertCurrency({
        amount: tm.eligibleInvoiceAmount,
        exchangeRates,
        from: tm.currency,
        to: 'USD',
      });
      acc.invoiceCount += tm.eligibleInvoiceCount;
      return acc;
    },
    { eligibleInvoiceAmount: 0, invoiceCount: 0 }
  );

  return computeAggregateSubmitStats({
    eligibleInvoiceAmount: totals.eligibleInvoiceAmount,
    discount: 0,
    estimatedEarlyPaymentAmount: totals.eligibleInvoiceAmount,
    invoiceCount: totals.invoiceCount,
    currency: 'USD',
    exchangeRates,
  });
};

/**
 * Fires per-market track events for a global participation toggle.
 * Emits participation::submitted, participation::variable-rate::submitted, or participation::fixed-rate::submitted
 * depending on the pricing type.
 */
export const trackParticipationMarketEvents = ({
  takerMarkets,
  pricingType,
  isEnabled,
  exchangeRates,
  submissionBatchId,
  trackEnterpriseEvent,
}: {
  takerMarkets: TakerMarket[];
  pricingType: GlobalOfferMarketPricingType;
  isEnabled: boolean;
  exchangeRates: ExchangeRates;
  submissionBatchId: string;
  trackEnterpriseEvent: ReturnType<typeof useReporting>['trackEnterpriseEvent'];
}) => {
  takerMarkets
    .filter((tm) => isOfferValidForUpdate(tm, isEnabled) && tm.offerConfig.isEnabled !== isEnabled)
    .forEach((tm) => {
      const marketStats = computeMarketSubmitStats({
        takerMarket: tm,
        rate: 0,
        rateType: tm.offerConfig.isDiscountBidding ? 'DISC' : 'APR',
        exchangeRates,
      });
      const payload = {
        marketUuid: tm.marketUuid,
        supplierDivisionUuid: tm.takerDivisionUuid,
        isEnabled,
        submissionBatchId,
        isDraft: false,
        isEdit: isEnabled && !!(tm.offerConfig.uuid && tm.offerConfig.isEnabled),
        isManuallyAwarded: tm.offerConfig.isManuallyAwarded,
        isPreferred: tm.offerConfig.offerType === 'PREFERRED_TERM',
        ...marketStats,
      };

      if (pricingType === 'BENCHMARK') {
        trackEnterpriseEvent('participation::variable-rate::submitted', payload);
      } else if (pricingType === 'FIXED') {
        trackEnterpriseEvent('participation::fixed-rate::submitted', payload);
      } else {
        trackEnterpriseEvent('participation::submitted', payload);
      }
    });
};

// Editing this function so that the rate must be greater than the minThreshold and less than or equal to the maxRate
// This change is needed so that the rate must be greater than zero
const rateWithinLimits = ({
  rate,
  minThreshold,
  maxRate,
}: {
  rate: string | null;
  minThreshold: number;
  maxRate: number;
}) => !!rate && !isNaN(parseFloat(rate)) && parseFloat(rate) > minThreshold && parseFloat(rate) <= maxRate;

export const validateRate = ({ rate, rateType, offerLimits }: ValidateRateInput): boolean =>
  rateWithinLimits({
    rate,
    ...(rateType === 'disc'
      ? {
          minThreshold: offerLimits.disc.minThreshold,
          maxRate: offerLimits.disc.maxRate,
        }
      : {
          minThreshold: offerLimits.apr.minThreshold,
          maxRate: offerLimits.apr.maxRate,
        }),
  });

export const getRateFromTakerMarket = (takerMarket: TakerMarket) => {
  return takerMarket.offerConfig.uuid && takerMarket.offerConfig.isDiscountBidding
    ? takerMarket.offerConfig.maxDiscount ?? 0
    : takerMarket.offerConfig.uuid && takerMarket.offerConfig.maxApr
    ? takerMarket.offerConfig.maxApr
    : 0;
};

export const hasMismatchedRateTypes = (
  takerMarkets: TakerMarket[],
  selectedRateType: 'apr' | 'disc' | undefined
): boolean => {
  // no existing rates
  if (takerMarkets.every((takerMarket) => !takerMarket.offerConfig.maxDiscount && !takerMarket.offerConfig.maxApr)) {
    return false;
  }

  return takerMarkets.some((takerMarket) => {
    // Skip net new offers that don't have existing rates
    if (!takerMarket.offerConfig.uuid || takerMarket.offerConfig.isDiscountBidding === null) {
      return false;
    }

    const originalRateType = takerMarket.offerConfig.isDiscountBidding ? 'disc' : 'apr';
    return originalRateType !== selectedRateType;
  });
};
